import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { Button, TextField, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import {
  generateKey,
  deletePoster,
  getGamesData,
  getGame,
  saveGameSettings,
  getLinks,
  loadPoster,
  getProfile,
} from "../redux/asyncThunk/userActions";
import InputFileUpload from "./UploadFile";
import formatDateToDDMMYYYY, { formatDateTimeRu } from "../utils/formatDate";
import { sanitizePlainText } from "../utils/inputValidation";
import {
  ACCESS_MODE_HINTS,
  EXAMPLE_START_POST,
  getGroupShareLink,
  getProjectStatusFromSettings,
  settingsFromProjectStatus,
} from "../utils/projectAccess";
import { userSlice } from "../redux/user/userSlice";
import { AUTH_FIELD_SX } from "../pages/AuthLayout";
import "./index.css";

const { addMessage } = userSlice;

const prodURL = "https://club-lab.ru";
const url =
  window.location.hostname === "localhost" ? "http://localhost:3004" : prodURL;

const fieldSx = { ...AUTH_FIELD_SX, maxWidth: "100%", width: "100%" };

const DESCRIPTION_PLACEHOLDER =
  "Этот сценарий поможет освоить вам ремонт кофемашины Макита. Вы изучите как починить и не сломать.";

export default function Settings() {
  const dispatch = useDispatch();
  const [data, setData] = useState();
  const [settings, setSettings] = useState({
    title: "",
    category: "",
    description: "",
    private_access: true,
    adult_content: false,
    allow_replay: true,
    completion_text: "",
    author_telegram: "",
    projectStatus: "draft",
    accessMode: "individual",
  });
  const [copied, setCopied] = useState(null);

  const game = useSelector((state) => state.user.game);
  const user = useSelector((state) => state.user?.data?.user);
  const keys = useSelector((state) => state.user?.keys);

  const shareLink = game?._id ? getGroupShareLink(game._id) : "";
  const isIndividualAccess = settings.accessMode === "individual";
  const projectKeys = keys?.filter((el) => el.game === data?._id) ?? [];

  useEffect(() => {
    if (!game) return;

    setSettings({
      title: game.title ?? "",
      category: game.category ?? "",
      description: game.description ?? "",
      private_access: game.settings?.private_access !== false,
      adult_content: Boolean(game.settings?.adult_content),
      allow_replay: game.settings?.allow_replay !== false,
      completion_text: game.settings?.completion_text ?? "",
      author_telegram: game.settings?.author_telegram ?? "",
      projectStatus: getProjectStatusFromSettings(game.settings),
      accessMode:
        game.settings?.private_access === false ? "group" : "individual",
    });
    setData({
      ...game,
      scenesValue: game?.nodes?.filter((el) => el.type === "group")?.length,
    });
  }, [game]);

  const handleChange = (event, property) => {
    setSettings((prev) => ({
      ...prev,
      [property]:
        typeof event.target.value === "string"
          ? sanitizePlainText(
              event.target.value,
              property === "description" || property === "completion_text"
                ? 2000
                : 200
            )
          : event.target.value,
    }));
  };

  const handleToggle = (property) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [property]: event.target.checked,
    }));
  };

  const buildSettingsPayload = (overrides = {}) => {
    const statusFlags = settingsFromProjectStatus(settings.projectStatus);
    const private_access =
      overrides.accessMode !== undefined
        ? overrides.accessMode === "individual"
        : settings.accessMode === "individual";

    return {
      private_access,
      adult_content: settings.adult_content,
      published: statusFlags.published,
      hidden: statusFlags.hidden,
      completion_text: settings.completion_text,
      author_telegram: settings.author_telegram,
      allow_replay: settings.allow_replay,
    };
  };

  const handleUpdateSettings = () => {
    dispatch(
      saveGameSettings({
        game: game._id,
        title: settings.title,
        description: settings.description,
        category: settings.category,
        settings: buildSettingsPayload(),
        onSucces: () => {
          dispatch(getLinks({ userId: user.id }));
          dispatch(getGame({ gameId: game._id }));
        },
      })
    );
  };

  const handleAccessModeChange = async (event) => {
    const accessMode = event.target.value;
    const private_access = accessMode === "individual";
    const previousMode = settings.accessMode;

    setSettings((prev) => ({
      ...prev,
      accessMode,
      private_access,
    }));

    const result = await dispatch(
      saveGameSettings({
        game: game._id,
        title: settings.title,
        description: settings.description,
        category: settings.category,
        settings: buildSettingsPayload({ accessMode }),
        onSucces: () => {
          dispatch(getLinks({ userId: user.id }));
          dispatch(getProfile({ id: user.id }));
        },
      })
    );

    if (saveGameSettings.rejected.match(result)) {
      setSettings((prev) => ({
        ...prev,
        accessMode: previousMode,
        private_access: previousMode === "individual",
      }));
    }
  };

  const handleAddPoster = (event, id) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = async () => {
      const base64String = reader.result.split(",")[1];
      dispatch(
        loadPoster({
          gameId: id,
          base64String,
          onSucces: () => dispatch(getGame({ gameId: game._id })),
        })
      );
    };
  };

  const handleCopy = async (text, copyId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(copyId);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleShowExamplePost = () => {
    dispatch(
      addMessage({
        type: "start-post",
        preview: EXAMPLE_START_POST,
      })
    );
  };

  const posterTitle = settings.title || "Название вашего сценария";
  const posterDescription =
    settings.description || DESCRIPTION_PLACEHOLDER;

  return (
    <div className="app-editor-settings">
      <div className="app-editor-settings__top">
        <section className="app-settings-card app-settings-card--meta">
          <h3 className="app-settings-card__title">Основное</h3>

          <div className="app-settings-meta-grid">
            <div className="app-settings-field app-settings-field--id">
              <span className="app-settings-field__label">ID сценария</span>
              <span className="app-settings-id">{game?._id}</span>
            </div>

            <div className="app-settings-field">
              <span className="app-settings-field__label">
                Последнее изменение
              </span>
              <span className="app-settings-meta">
                {formatDateTimeRu(game?.updatedAt || game?.createdAt)}
              </span>
            </div>
          </div>

          <div className="app-settings-field app-settings-field--status">
            <span className="app-settings-field__label">Статус проекта</span>
            <RadioGroup
              row
              className="app-settings-status"
              value={settings.projectStatus}
              onChange={(event) => handleChange(event, "projectStatus")}
            >
              <FormControlLabel
                value="published"
                control={<Radio size="small" />}
                label="Опубликован"
                className="app-settings-status__option"
              />
              <FormControlLabel
                value="draft"
                control={<Radio size="small" />}
                label="В работе"
                className="app-settings-status__option"
              />
              <FormControlLabel
                value="hidden"
                control={<Radio size="small" />}
                label="Остановлен"
                className="app-settings-status__option"
              />
            </RadioGroup>
          </div>

          <div className="app-settings-save">
            <Button
              variant="contained"
              size="small"
              className="app-btn app-btn--primary app-btn--compact"
              onClick={handleUpdateSettings}
            >
              Сохранить
            </Button>
          </div>
        </section>

        <section className="app-settings-card app-settings-card--client">
          <h3 className="app-settings-card__title">Поведение для клиента</h3>

          <div className="app-settings-client-grid">
            <div className="app-settings-toggle">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={settings.adult_content}
                    onChange={handleToggle("adult_content")}
                  />
                }
                label="Контент 18+"
              />
              <p className="app-settings-toggle__hint">
                Доступ только для пользователей с подтверждённым возрастом 18+.
              </p>
            </div>

            <div className="app-settings-toggle">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={settings.allow_replay}
                    onChange={handleToggle("allow_replay")}
                  />
                }
                label="Разрешить повторное прохождение"
              />
              <p className="app-settings-toggle__hint">
                Если выключено, клиент не сможет пройти сценарий заново.
              </p>
            </div>

            <div className="app-settings-field app-settings-field--wide">
              <label
                className="app-settings-field__label"
                htmlFor="completion_text"
              >
                Текст при завершении
              </label>
              <TextField
                id="completion_text"
                placeholder="Сообщение бота в конце сценария"
                variant="outlined"
                multiline
                minRows={2}
                fullWidth
                value={settings.completion_text}
                onChange={(event) => handleChange(event, "completion_text")}
                sx={fieldSx}
              />
            </div>

            <div className="app-settings-field app-settings-field--wide">
              <label
                className="app-settings-field__label"
                htmlFor="author_telegram"
              >
                Связаться с автором
              </label>
              <TextField
                id="author_telegram"
                placeholder="@username или https://t.me/username"
                variant="outlined"
                size="small"
                fullWidth
                value={settings.author_telegram}
                onChange={(event) => handleChange(event, "author_telegram")}
                sx={fieldSx}
              />
              <p className="app-settings-field__hint">
                Telegram-ссылка для кнопки «Связаться с автором» в боте.
              </p>
            </div>
          </div>
        </section>

        <section className="app-settings-card app-settings-card--poster">
          <h3 className="app-settings-card__title">
            Стартовый пост вашего сценария
          </h3>

          <div className="app-settings-poster-grid">
            <div className="app-settings-poster-fields">
              <div className="app-settings-field">
                <label className="app-settings-field__label" htmlFor="title">
                  Название
                </label>
                <TextField
                  onChange={(event) => handleChange(event, "title")}
                  id="title"
                  placeholder="Название вашего сценария"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={settings?.title ?? ""}
                  sx={fieldSx}
                />
              </div>

              <div className="app-settings-field">
                <label
                  className="app-settings-field__label"
                  htmlFor="description"
                >
                  Текст стартового поста
                </label>
                <TextField
                  id="description"
                  placeholder={DESCRIPTION_PLACEHOLDER}
                  variant="outlined"
                  multiline
                  minRows={3}
                  fullWidth
                  value={settings?.description ?? ""}
                  onChange={(event) => handleChange(event, "description")}
                  sx={fieldSx}
                />
              </div>

              <div className="app-settings-poster__actions">
                <InputFileUpload
                  handleAddPoster={(event) => handleAddPoster(event, game._id)}
                />
                <Button
                  size="small"
                  variant="outlined"
                  className="app-btn app-btn--secondary app-btn--compact"
                  startIcon={<VisibilityOutlinedIcon />}
                  onClick={handleShowExamplePost}
                >
                  Пример стартового поста
                </Button>
              </div>
            </div>

            <div className="app-settings-poster">
              {data?.poster ? (
                <Tooltip title="Удалить постер" placement="top">
                  <DeleteIcon
                    className="app-settings-poster__delete"
                    onClick={() => {
                      dispatch(
                        deletePoster({
                          gameId: game._id,
                          onSucces: () =>
                            dispatch(getGamesData({ id: user.id })),
                        })
                      );
                    }}
                  />
                </Tooltip>
              ) : null}

              <div className="app-settings-poster__media">
                {data?.poster ? (
                  <img src={`${url}/${data?.poster}`} alt="Постер сценария" />
                ) : (
                  <div className="app-settings-poster__empty">
                    <ImageNotSupportedOutlinedIcon />
                    <span>Загрузите изображение</span>
                  </div>
                )}
              </div>

              <div className="app-settings-poster__caption">
                <p className="app-settings-poster__caption-title">
                  Название: {posterTitle}
                </p>
                <p>{posterDescription}</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="app-settings-card app-settings-card--access">
        <h3 className="app-settings-card__title">Доступ к проекту</h3>

        <RadioGroup
          row
          className="app-access-segment"
          value={settings.accessMode}
          onChange={handleAccessModeChange}
        >
          <FormControlLabel
            value="individual"
            control={<Radio size="small" sx={{ display: "none" }} />}
            className={`app-access-segment__option${
              isIndividualAccess ? " app-access-segment__option--active" : ""
            }`}
            label={
              <>
                <VpnKeyOutlinedIcon fontSize="small" />
                <span>Индивидуальный</span>
              </>
            }
          />
          <FormControlLabel
            value="group"
            control={<Radio size="small" sx={{ display: "none" }} />}
            className={`app-access-segment__option${
              !isIndividualAccess ? " app-access-segment__option--active" : ""
            }`}
            label={
              <>
                <GroupsOutlinedIcon fontSize="small" />
                <span>Групповой</span>
              </>
            }
          />
        </RadioGroup>

        <p className="app-access-segment__hint">
          {ACCESS_MODE_HINTS[settings.accessMode]}
        </p>

        {isIndividualAccess ? (
          <>
            <div className="app-editor-keys__actions">
              <Button
                size="small"
                variant="contained"
                className="app-btn app-btn--primary app-btn--compact"
                onClick={() =>
                  dispatch(
                    generateKey({
                      game: data._id,
                      user: data.owner,
                    })
                  )
                }
              >
                Сгенерировать ключ
              </Button>
            </div>

            <div className="app-table-wrap">
              <table className="app-table app-table--keys">
                <thead>
                  <tr>
                    <th>Ключ</th>
                    <th>Использован</th>
                    <th>Дата использования</th>
                  </tr>
                </thead>
                <tbody>
                  {projectKeys.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="app-table__empty">
                        Ключей пока нет
                      </td>
                    </tr>
                  ) : (
                    projectKeys.map((el) => (
                      <tr key={el.key}>
                        <td>
                          <div className="app-table__key-cell">
                            <span>{el.key}</span>
                            <Tooltip
                              title={
                                copied === el.key ? "Скопировано" : "Копировать"
                              }
                              placement="top"
                            >
                              <ContentCopyIcon
                                sx={{ cursor: "pointer", fontSize: 18 }}
                                onClick={() => handleCopy(el.key, el.key)}
                              />
                            </Tooltip>
                          </div>
                        </td>
                        <td>{el.availeble ? "Нет" : "Да"}</td>
                        <td>
                          {el.usageDate
                            ? formatDateToDDMMYYYY(el.usageDate)
                            : "Не использован"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            <p className="app-access-segment__hint app-access-segment__hint--team">
              Групповой доступ работает при активной бизнес-подписке. Выберите
              тариф по числу сотрудников.{" "}
              <Link to="/main/price">Тарифы для бизнеса</Link>
            </p>
            <div className="app-editor-access__link-row">
            <LinkOutlinedIcon className="app-editor-access__link-icon" />
            <span className="app-editor-access__link">{shareLink}</span>
            <Tooltip
              title={copied === "link" ? "Скопировано" : "Копировать ссылку"}
              placement="top"
            >
              <Button
                size="small"
                variant="outlined"
                className="app-btn app-btn--secondary app-btn--compact"
                onClick={() => handleCopy(shareLink, "link")}
              >
                Копировать
              </Button>
            </Tooltip>
          </div>
          </>
        )}
      </section>
    </div>
  );
}
